import {
	Alert,
	Box,
	Button,
	FileDropArea,
	Icons,
	Inline,
	Spinner,
	Stack,
	Text,
	usePastedFiles,
} from "@sembark-travel/ui-base"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
} from "@sembark-travel/ui-dialog"
import {
	addKeyToFieldArrayItem,
	arrayMutators,
	FieldArray,
	FileInputField,
	Form,
	GetFieldValue,
	getFieldArrayItemKey,
	SelectField,
	SelectInputField,
	SubmissionError,
	TextAreaInputField,
	TextInputField,
	useForm,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import { useMemo, useState } from "react"
import {
	SelectWAFlow,
	SelectWAFlowScreen,
	type TWAFlow,
} from "../Integrations/MessagingGateways"
import {
	QUICK_SEND_ACCEPT,
	QUICK_SEND_FLOW_CTA_MAX_LENGTH,
	QUICK_SEND_MAX_MESSAGES,
	QUICK_SEND_MESSAGE_TYPE_LABELS,
	QUICK_SEND_MESSAGE_TYPES,
	quickSendFormValues,
	quickSendTypeForFile,
	useFetchWAQuickSend,
	WAQuickSendsXHR,
	type TWAQuickSend,
	type TWAQuickSendFormValues,
	type TWAQuickSendMediaType,
	type TWAQuickSendMessageFormValue,
	type TWAQuickSendMessageType,
	usableSavedFile,
} from "./store"

const TEXT_MAX_LENGTH = 4096
const CAPTION_MAX_LENGTH = 1024
const FLOW_BODY_MAX_LENGTH = 1024

/** Every file a quick send takes, whatever kind of message it makes. */
const QUICK_SEND_FILE_ACCEPT = Object.values(QUICK_SEND_ACCEPT).join(",")

function newMessage(
	type: TWAQuickSendMessageType = "text",
	file: File | null = null
): TWAQuickSendMessageFormValue {
	return addKeyToFieldArrayItem({ type, body: "", file })
}

/**
 * Why WhatsApp will not send a file in a quick send, naming what it does
 * send.
 */
function unsupportedFileMessage(file: File): string {
	return `WhatsApp does not send ${file.name} in a quick send. Add a JPEG or PNG image, an MP4 or 3GP video, or a PDF, Word, Excel, PowerPoint or text file.`
}

/** Create a quick send. */
export function CreateWAQuickSendForm({
	onDone,
	onCancel,
}: {
	onDone: (quickSend: TWAQuickSend) => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const initialValues = useMemo<TWAQuickSendFormValues>(
		() => ({ name: "", messages: [newMessage()] }),
		[]
	)

	return (
		<WAQuickSendForm
			initialValues={initialValues}
			submitLabel="Create Quick Send"
			onCancel={onCancel}
			onSubmit={async (values) => {
				const quickSend = await WAQuickSendsXHR(xhr).create(values)
				showSnackbar(`"${quickSend.name}" created.`)
				onDone(quickSend)
			}}
		/>
	)
}

/** Edit a saved quick send. */
export function EditWAQuickSendForm({
	quickSendId,
	onDone,
	onCancel,
}: {
	quickSendId: number | string
	onDone: (quickSend: TWAQuickSend) => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const { data, error } = useFetchWAQuickSend(quickSendId, {
		revalidateOnFocus: false,
	})
	const initialValues = useMemo<TWAQuickSendFormValues | null>(() => {
		if (!data) return null
		const values = quickSendFormValues(data)
		return {
			...values,
			messages: values.messages.map((message) =>
				addKeyToFieldArrayItem(message)
			),
		}
	}, [data])

	if (error) {
		return (
			<Alert status="error">
				{error.message || "Unable to load this quick send."}
			</Alert>
		)
	}

	if (!data || !initialValues) {
		return <Spinner padding="8" alignCenter />
	}

	return (
		<WAQuickSendForm
			initialValues={initialValues}
			submitLabel="Save Quick Send"
			onCancel={onCancel}
			onSubmit={async (values) => {
				const quickSend = await WAQuickSendsXHR(xhr).update(data.id, values)
				showSnackbar(`"${quickSend.name}" saved.`)
				onDone(quickSend)
			}}
		/>
	)
}

function validate(values: TWAQuickSendFormValues) {
	const errors: Record<string, unknown> = {}

	if (!values.name?.trim()) {
		errors.name = "Name the quick send so agents can find it."
	}

	const messages = values.messages ?? []

	if (messages.length === 0) {
		errors.messages = "Add at least one message."
	}

	const messageErrors = messages.map((message) => {
		const body = message.body?.trim() ?? ""

		if (message.type === "text") {
			if (!body) return { body: "Write the message." }
			if (body.length > TEXT_MAX_LENGTH) {
				return {
					body: `WhatsApp sends at most ${TEXT_MAX_LENGTH} characters in a message.`,
				}
			}
			return undefined
		}

		const rowErrors: Record<string, string> = {}

		if (message.type === "flow") {
			const cta = message.flowCta?.trim() ?? ""

			if (!message.flow) rowErrors.flow = "Pick the form to send."
			if (!cta || cta.length > QUICK_SEND_FLOW_CTA_MAX_LENGTH) {
				rowErrors.flowCta = `Write the button label, in at most ${QUICK_SEND_FLOW_CTA_MAX_LENGTH} characters.`
			}
			if (!body) {
				rowErrors.body = "Write the message above the button."
			} else if (body.length > FLOW_BODY_MAX_LENGTH) {
				rowErrors.body = `WhatsApp sends at most ${FLOW_BODY_MAX_LENGTH} characters above a form's button.`
			}
			if (message.flow && !message.flowScreen) {
				rowErrors.flowScreen = "Pick the screen the form opens on."
			}

			return Object.keys(rowErrors).length ? rowErrors : undefined
		}

		if (body.length > CAPTION_MAX_LENGTH) {
			rowErrors.body = `WhatsApp sends at most ${CAPTION_MAX_LENGTH} characters in a caption.`
		}

		if (!message.file && !usableSavedFile(message)) {
			rowErrors.file = `Attach the ${message.type} to send.`
		}

		return Object.keys(rowErrors).length ? rowErrors : undefined
	})

	if (messageErrors.some(Boolean)) {
		errors.messages = messageErrors
	}

	return errors
}

function WAQuickSendForm({
	initialValues,
	submitLabel,
	onSubmit,
	onCancel,
}: {
	initialValues: TWAQuickSendFormValues
	submitLabel: string
	onSubmit: (values: TWAQuickSendFormValues) => Promise<void>
	onCancel: () => void
}) {
	return (
		<Form<TWAQuickSendFormValues>
			initialValues={initialValues}
			validate={validate}
			onSubmit={withServerErrors(onSubmit)}
			mutators={{ ...arrayMutators }}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting, form }) => (
				<form onSubmit={handleSubmit} noValidate>
					<Stack gap="8">
						<Box maxWidth="lg">
							<TextInputField
								name="name"
								label="Name"
								required
								placeholder="Payment details"
								help="What agents search for when they pick one from the composer."
							/>
						</Box>
						<Stack gap="3">
							<Stack gap="1">
								<Text fontWeight="semibold">Messages</Text>
								<Text color="muted" fontSize="sm">
									Sent in this order, straight after one another. Free-form
									messages only reach a customer within 24 hours of their last
									message to you.
								</Text>
								<Text color="muted" fontSize="sm">
									<Icons.PaperClip /> Drop images, videos or documents here, as
									many as you like, to add each one as a message. Or paste a
									screenshot into a message to attach it.
								</Text>
							</Stack>
							<FieldArray<TWAQuickSendMessageFormValue> name="messages">
								{({ fields }) => (
									<FileDropArea
										accept={QUICK_SEND_FILE_ACCEPT}
										multiple
										label="Drop files to add them as messages"
										rejectionMessage={unsupportedFileMessage}
										onFiles={(files) =>
											addMessagesFor(files, fields.length ?? 0, (message) =>
												fields.push(message)
											)
										}
									>
										<Stack gap="4">
											{fields.map((name, index) => (
												<MessageRow
													key={getFieldArrayItemKey(form as never, name)}
													name={name}
													index={index}
													count={fields.length ?? 0}
													onMoveUp={() => fields.move(index, index - 1)}
													onMoveDown={() => fields.move(index, index + 1)}
													onRemove={() => fields.remove(index)}
												/>
											))}
											<Box>
												<Button
													size="sm"
													level="secondary"
													disabled={
														(fields.length ?? 0) >= QUICK_SEND_MAX_MESSAGES
													}
													onClick={() => fields.push(newMessage())}
												>
													<Icons.Plus /> Add Message
												</Button>
												{(fields.length ?? 0) >= QUICK_SEND_MAX_MESSAGES ? (
													<Text color="muted" fontSize="sm">
														A quick send holds at most {QUICK_SEND_MAX_MESSAGES}{" "}
														messages.
													</Text>
												) : null}
											</Box>
										</Stack>
									</FileDropArea>
								)}
							</FieldArray>
						</Stack>
						<Stack gap="4">
							<SubmissionError />
							<Inline gap="4">
								<Button type="submit" level="primary" disabled={submitting}>
									{submitting ? "Saving..." : submitLabel}
								</Button>
								<Button disabled={submitting} onClick={onCancel}>
									Cancel
								</Button>
							</Inline>
						</Stack>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function MessageRow({
	name,
	index,
	count,
	onMoveUp,
	onMoveDown,
	onRemove,
}: {
	name: string
	index: number
	count: number
	onMoveUp: () => void
	onMoveDown: () => void
	onRemove: () => void
}) {
	return (
		<MessagePasteArea name={name} position={index + 1}>
			<Box borderWidth="1" rounded="md" padding="4">
				<Inline gap="3" alignItems="start">
					<Inline
						justifyContent="center"
						alignItems="center"
						size="8"
						bgColor="subtle"
						rounded="full"
						borderWidth="1"
						flexShrink="0"
					>
						<Text fontWeight="semibold">{index + 1}</Text>
					</Inline>
					<Stack gap="3" flex="1">
						<Box maxWidth="xs">
							<SelectInputField name={`${name}.type`} label="Kind">
								{QUICK_SEND_MESSAGE_TYPES.map((type) => (
									<option key={type} value={type}>
										{QUICK_SEND_MESSAGE_TYPE_LABELS[type]}
									</option>
								))}
							</SelectInputField>
						</Box>
						<GetFieldValue<TWAQuickSendMessageType> name={`${name}.type`}>
							{({ value: type }) =>
								type === "text" ? (
									<TextAreaInputField
										key="text"
										name={`${name}.body`}
										label="Message"
										required
										rows={4}
									/>
								) : type === "flow" ? (
									<FlowFields key="flow" name={name} />
								) : (
									// Keyed by kind so a file picked for one kind is
									// not carried into another's input.
									<Stack key={type} gap="3">
										<GetFieldValue<TWAQuickSendMessageFormValue> name={name}>
											{({ value: row }) => {
												const savedFile = row ? usableSavedFile(row) : null

												return (
													<Stack gap="1">
														<FileInputField
															name={`${name}.file`}
															label={QUICK_SEND_MESSAGE_TYPE_LABELS[type]}
															accept={QUICK_SEND_ACCEPT[type]}
															required={!savedFile}
															help={fileHelp(type)}
														/>
														{savedFile ? (
															<Text fontSize="sm" color="muted">
																Current file:{" "}
																<a
																	href={savedFile.url}
																	target="_blank"
																	rel="noopener noreferrer"
																>
																	{savedFile.file_name}
																</a>
																. Choose a new one to replace it.
															</Text>
														) : null}
													</Stack>
												)
											}}
										</GetFieldValue>
										<TextAreaInputField
											name={`${name}.body`}
											label="Caption"
											rows={2}
											help="Optional. Sent with the file."
										/>
									</Stack>
								)
							}
						</GetFieldValue>
					</Stack>
					<Stack gap="1">
						<Button
							size="sm"
							level="tertiary"
							title="Move up"
							disabled={index === 0}
							onClick={onMoveUp}
						>
							<Icons.ChevronDown rotate="180" />
						</Button>
						<Button
							size="sm"
							level="tertiary"
							title="Move down"
							disabled={index === count - 1}
							onClick={onMoveDown}
						>
							<Icons.ChevronDown />
						</Button>
						<Button
							size="sm"
							level="tertiary"
							title="Remove message"
							disabled={count === 1}
							onClick={onRemove}
						>
							<Icons.Trash />
						</Button>
					</Stack>
				</Inline>
			</Box>
		</MessagePasteArea>
	)
}

/**
 * Each dropped or pasted file as a new message of the kind WhatsApp sends it
 * as, up to the most a quick send holds. Says how many were left out.
 */
function addMessagesFor(
	files: Array<File>,
	count: number,
	push: (message: TWAQuickSendMessageFormValue) => void
): string | void {
	let room = QUICK_SEND_MAX_MESSAGES - count
	let leftOut = 0

	for (const file of files) {
		// The drop area admits only the kinds this names.
		const type = quickSendTypeForFile(file)
		if (!type) continue
		if (room > 0) {
			push(newMessage(type, file))
			room -= 1
		} else {
			leftOut += 1
		}
	}

	if (leftOut) {
		return `A quick send holds at most ${QUICK_SEND_MAX_MESSAGES} messages, so ${leftOut} ${leftOut === 1 ? "file was" : "files were"} left out.`
	}
}

/**
 * A message that takes a pasted file — a screenshot, say — into its file
 * field. A file of another kind than the message, or pasted into a text
 * message, would change what the message is, so that is asked first.
 */
function MessagePasteArea({
	name,
	position,
	children,
}: {
	name: string
	position: number
	children: React.ReactNode
}) {
	return (
		<ConfirmationDialogProvider>
			{({ confirm }) => (
				<MessagePasteAreaContent
					name={name}
					position={position}
					confirm={confirm}
				>
					{children}
				</MessagePasteAreaContent>
			)}
		</ConfirmationDialogProvider>
	)
}

function MessagePasteAreaContent({
	name,
	position,
	confirm,
	children,
}: {
	name: string
	position: number
	confirm: () => Promise<boolean>
	children: React.ReactNode
}) {
	const form = useForm()
	const [pasteError, setPasteError] = useState<string | null>(null)
	const [switchTo, setSwitchTo] = useState<{
		type: TWAQuickSendMediaType
		file: File
	} | null>(null)

	async function paste(files: FileList, rejected: Array<File>) {
		if (files.length > 1) {
			setPasteError("A message carries one file. Paste one file.")
			return
		}
		if (rejected.length) {
			setPasteError(unsupportedFileMessage(rejected[0]))
			return
		}
		setPasteError(null)

		const file = files[0]
		// Accepted, so it is one of the kinds this names.
		const type = quickSendTypeForFile(file)
		if (!type) return

		if (form.getFieldState(`${name}.type`)?.value === type) {
			form.change(`${name}.file`, file)
			return
		}

		setSwitchTo({ type, file })
		const isConfirmed = await confirm()
		setSwitchTo(null)
		if (!isConfirmed) return

		form.batch(() => {
			form.change(`${name}.type`, type)
			form.change(`${name}.file`, file)
		})
	}

	const { pasteHandlers } = usePastedFiles(paste, QUICK_SEND_FILE_ACCEPT)

	return (
		<Box {...pasteHandlers}>
			<Stack gap="2">
				{pasteError ? (
					<Text color="danger" fontSize="sm" role="alert">
						{pasteError}
					</Text>
				) : null}
				{children}
			</Stack>
			<ConfirmationDialog
				title={`Make message ${position} ${
					switchTo
						? articled(QUICK_SEND_MESSAGE_TYPE_LABELS[switchTo.type])
						: ""
				}?`}
				acceptActionLabel={`Switch to ${
					switchTo ? QUICK_SEND_MESSAGE_TYPE_LABELS[switchTo.type] : ""
				}`}
			>
				{switchTo ? (
					<Text>
						{switchTo.file.name} goes out as{" "}
						{articled(
							QUICK_SEND_MESSAGE_TYPE_LABELS[switchTo.type].toLowerCase()
						)}
						. Message {position} will be switched to one, with the file
						attached, and its text kept as the caption.
					</Text>
				) : null}
			</ConfirmationDialog>
		</Box>
	)
}

function articled(word: string): string {
	return /^[aeiou]/i.test(word) ? `an ${word}` : `a ${word}`
}

/**
 * A form message: one of the tenant's published Flows behind a button, with
 * the text above it. Any of the tenant's Flows can be picked, but a number
 * can only send the Flows of its own business account, which is checked
 * when the quick send goes out.
 */
function FlowFields({ name }: { name: string }) {
	const form = useForm()

	return (
		<Stack gap="3">
			<GetFieldValue<TWAQuickSendMessageFormValue["flow"]>
				name={`${name}.flow`}
			>
				{({ value: pickedFlow }) => (
					<>
						<SelectField
							select={SelectWAFlow}
							name={`${name}.flow`}
							label="Form"
							placeholder="Select a published Flow"
							required
							help="Flows are authored in Meta's Flow Builder. A form reaches the customer only from a number on the Flow's business account."
							onChange={(value: unknown) => {
								const flow = (Array.isArray(value) ? value[0] : value) as
									| TWAFlow
									| null
									| undefined
								form.change(`${name}.flow`, flow ?? null)
								// A screen belongs to one Flow; the screen picker
								// selects the new Flow's first.
								form.change(`${name}.flowScreen`, null)
							}}
						/>
						<SelectField
							key={pickedFlow?.id ?? ""}
							select={SelectWAFlowScreen}
							name={`${name}.flowScreen`}
							label="Opening Screen"
							flowId={pickedFlow?.id}
							autoSelectFirst
							placeholder={
								pickedFlow ? "Select the opening screen" : "Pick the form first"
							}
							disabled={!pickedFlow}
							help="The screen the form opens on. The Flow's first screen is picked for you."
						/>
					</>
				)}
			</GetFieldValue>
			<TextAreaInputField
				name={`${name}.body`}
				label="Message"
				required
				rows={3}
				placeholder="Tell us about your trip."
				help="Shown above the button that opens the form."
			/>
			<Box maxWidth="xs">
				<TextInputField
					name={`${name}.flowCta`}
					label="Button Label"
					required
					placeholder="Share details"
					maxLength={QUICK_SEND_FLOW_CTA_MAX_LENGTH}
				/>
			</Box>
		</Stack>
	)
}

function fileHelp(type: TWAQuickSendMediaType): string {
	switch (type) {
		case "image":
			return "A JPEG or PNG, up to 5 MB."
		case "video":
			return "An MP4 or 3GP video, up to 16 MB."
		default:
			return "A PDF, Word, Excel, PowerPoint or text file."
	}
}
